import { useLocale } from "../../../hooks/useLocale";
import { displayText, englishText } from "../../../locales/display";
import { msg } from "../../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDateTime } from "src/utils/dateTime";
import { ReactNode, useState } from "react";
import styled from "styled-components";
import Decimal from "decimal.js";
import { ArrowRight, ArrowRightLeft, CircleCheck, CircleX, Clock3, Copy, CreditCard, FileJson, Globe2, Hash, KeyRound, ReceiptText, RotateCcw, ShieldCheck, UserRound } from "lucide-react";
import { IconVisa } from "@ui/icons/IconVisa";
import { IconMastercard } from "@ui/icons/IconMastercard";
import { CountryFlag } from "../icons/CountryFlag";

const Layout = styled.div`
	display: flex; flex-direction: column; gap: 24px; min-width: 0; flex-shrink: 0;
	font: 13px/1.5 "Euclid Circular B", sans-serif; color: #344054;
	* { box-sizing: border-box; }
	button { font: inherit; cursor: pointer; }
	button:focus-visible { outline: 2px solid #528bff; outline-offset: 3px; }
	.card { background: #fff; border: 1px solid #d0d5dd; border-radius: 12px; padding: 20px; box-shadow: 0 1px 2px rgba(16,24,40,.05); min-width: 0; }
	.summary { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; padding: 16px 20px; }
	.summary .eyebrow { margin-bottom: 6px; }
	.eyebrow, h3 { margin: 0 0 10px; color: #667085; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; }
	h3 { display: flex; align-items: center; gap: 7px; }
	h3 svg, .badge svg, .value-icon svg { flex-shrink: 0; }
	.value-icon { display: flex; align-items: center; gap: 8px; min-width: 0; }
	.value-icon > span { min-width: 0; overflow-wrap: anywhere; }
	.value-icon .symbol { display: inline-flex; align-items: center; color: #667085; flex-shrink: 0; }
	.amount { font-size: 32px; font-weight: 600; line-height: 1.2; color: #182230; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
	.amount.refund { color: #b42318; }
	.currency { margin-left: 8px; font-size: 18px; color: #667085; font-weight: 500; }
	.badges { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; align-items: center; }
	.badge { display: inline-flex; align-items: center; gap: 5px; padding: 2px 8px; border: 1px solid #d0d5dd; border-radius: 6px; background: #f9fafb; color: #475467; font-size: 12px; overflow-wrap: anywhere; }
	.badge[data-tone="success"] { color: #067647; border-color: #abefc6; background: #ecfdf3; }
	.badge[data-tone="error"] { color: #b42318; border-color: #fecdca; background: #fef3f2; }
	.badge[data-tone="pending"] { color: #b54708; border-color: #fedf89; background: #fffaeb; }
	.summary-side { display: flex; flex-direction: column; align-items: flex-end; gap: 12px; }
	.actions { display: flex; flex-wrap: wrap; gap: 8px; }
	.action { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 9px 12px; min-height: 38px; border: 1px solid #d0d5dd; border-radius: 8px; color: #344054; background: #fff; font-weight: 500; }
	.action:hover { background: #f9fafb; border-color: #98a2b3; }
	.action:disabled { opacity: .55; cursor: wait; }
	.dates { display: grid; gap: 4px; color: #98a2b3; font-size: 12px; text-align: right; }
	.dates span { color: #667085; margin-right: 8px; }
	.grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; align-items: stretch; }
	.grid > section { display: flex; flex-direction: column; }
	.grid > section > .card { flex: 1; }
	section { min-width: 0; }
	dl { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px 24px; margin: 0; }
	.field { min-width: 0; }
	.field.wide { grid-column: 1 / -1; }
	dt { color: #667085; font-size: 11px; margin-bottom: 5px; }
	dd { display: flex; align-items: flex-start; gap: 6px; margin: 0; min-width: 0; }
	.value { min-width: 0; overflow-wrap: anywhere; white-space: pre-wrap; }
	.token-not-tokenized { color: #b42318; }
	.empty { color: #98a2b3; }
	.copy { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 24px; width: 24px; height: 24px; margin-top: -2px; border: 0; border-radius: 5px; background: transparent; color: #98a2b3; }
	.copy:hover { color: #344054; background: #f2f4f7; }
	.failure { border: 1px solid #fecdca; background: #fff6f5; padding: 14px 18px; border-radius: 10px; color: #b42318; overflow-wrap: anywhere; }
	.failure strong { display: block; margin-bottom: 4px; font-weight: 600; }
	.fees { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
	.fees dt { margin-bottom: 8px; } .fees .value { font-size: 18px; font-weight: 600; font-variant-numeric: tabular-nums; }
	.table-card { overflow-x: auto; padding: 0; }
	table { width: 100%; border-collapse: collapse; text-align: left; white-space: nowrap; }
	th { font-size: 11px; font-weight: 500; color: #667085; background: #fcfcfd; }
	th, td { padding: 12px 16px; border-bottom: 1px solid #edf0f5; }
	td { font: inherit; color: #344054; }
	tbody tr:last-child td { border-bottom: 0; }
	tbody tr:hover { background: #f9fafb; }
	.payment-link { display: inline-flex; align-items: center; gap: 6px; border: 0; padding: 0; background: transparent; color: #175cd3; }
	.payment-link:hover { text-decoration: underline; }
	.notice { color: #667085; font-size: 12px; }
	@media(max-width: 700px) {
		.grid { grid-template-columns: minmax(0, 1fr); gap: 20px; }
		.summary { flex-direction: column; gap: 20px; }
		.summary-side { align-items: flex-start; } .dates { text-align: left; }
		.card { padding: 18px; } .table-card { padding: 0; }
		.amount { font-size: 28px; }
	}
	@media(max-width: 400px) { dl { grid-template-columns: minmax(0, 1fr); } .fees { grid-template-columns: minmax(0, 1fr); } }
`;

const display = (value: unknown) => value == null || value === "" ? "—" : String(value);
const dateText = (value: string) => {
	return formatDisplayDateTime(value, { separator: " · ", fallback: value });
};
const amount = (value: number, type: string) => {
	const refund = ["refund", "return"].includes(type.toLowerCase());
	const decimal = new Decimal(value).dividedBy(100);
	return refund ? `-${decimal.abs().toFixed(2)}` : decimal.toFixed(2);
};
const tone = (status: string) => {
	const normalized = status.toLowerCase();
	return ["completed", "success", "settled"].includes(normalized) ? "success"
		: ["failed", "error", "declined"].includes(normalized) ? "error"
		: ["pending", "processing", "created"].includes(normalized) ? "pending" : "neutral";
};
const Status = ({ value }: { value?: string | null }) => {
	useLocale();
	const statusTone = tone(value ?? "");
	const Icon = statusTone === "success" ? CircleCheck : statusTone === "error" ? CircleX : statusTone === "pending" ? Clock3 : null;
	return <span className="badge" data-tone={statusTone}>{Icon && <Icon size={12} aria-hidden="true" />}{displayText(display(value))}</span>;
};

const sectionIcons: Record<string, typeof CreditCard> = {
	"Payment identifiers": Hash,
	"Processing & settlement": ArrowRightLeft,
	Customer: UserRound,
	Card: CreditCard,
	Antifraud: ShieldCheck,
	Tokens: KeyRound,
};

const CountryValue = ({ country }: { country?: string }) => {
	useLocale();
	const code = country?.trim().toUpperCase() ?? "";
	if (!code) return <>—</>;
	return <div className="value-icon">
		<span className="symbol" aria-hidden="true">{/^[A-Z]{2}$/.test(code) ? <CountryFlag code={code} variant="rectangle" /> : <Globe2 size={16} />}</span>
		<span>{country}</span>
	</div>;
};

const NetworkValue = ({ network }: { network?: string }) => {
	useLocale();
	const normalized = network?.trim().toLowerCase().replace(/[\s_-]/g, "") ?? "";
	if (!normalized) return <>—</>;
	return <div className="value-icon">
		<span className="symbol" aria-hidden="true">{normalized === "visa" ? <IconVisa width={32} height={16} />
			: normalized === "mastercard" || normalized === "master" ? <IconMastercard width={24} height={16} />
			: <CreditCard size={17} />}</span>
		<span>{network?.toUpperCase()}</span>
	</div>;
};

/** Local Details design: no shared legacy components or their styles are changed. */
export function DectaSessionDetails({ session, relatedPayments = [], onInspect, onRefund, onRelated }: {
	session: IDectaSession; relatedPayments?: IDectaRelatedPayment[];
	onInspect: () => Promise<void>; onRefund: () => void; onRelated: (ssid: string) => void;
}) {
	useLocale();
	const [notice, setNotice] = useState("");
	const [inspecting, setInspecting] = useState(false);
	const copy = async (label: string, value: string) => {
		try { await navigator.clipboard.writeText(value); setNotice(msg("templateCopied_42116c7b", { v0: label })); }
		catch { setNotice(msg("couldNotCopySelectAndCopyTheValueManually")); }
	};
	const field = (label: string, value: unknown, copyable = false, wide = false, content?: ReactNode) => (
		<div className={`field${wide ? " wide" : ""}`} key={label}>
			<dt>{label}</dt><dd><div className={`value${value == null || value === "" ? " empty" : ""}`}>{content ?? display(value)}</div>
				{copyable && value != null && value !== "" && <button className="copy" type="button" title={msg("templateCopy_3f3ebff4", { v0: label })} aria-label={msg("templateCopy_3f3ebff4", { v0: label })} onClick={() => void copy(label, String(value))}><Copy size={13} /></button>}
			</dd>
		</div>
	);
	const section = (title: string, children: ReactNode) => {
		const Icon = sectionIcons[englishText(title)];
		return <section><h3>{Icon && <Icon size={14} aria-hidden="true" />}{title}</h3><div className="card"><dl>{children}</dl></div></section>;
	};
	const refundLike = ["refund", "return"].includes(session.type.toLowerCase());
	const antifraud = session.antifraud;
	return <Layout>
		<div className="card summary">
			<div>
				<div className="eyebrow">{msg("paymentAmount")}</div>
				<div className={`amount${refundLike ? " refund" : ""}`}>{amount(session.amount, session.type)}<span className="currency">{session.currency}</span></div>
				<div className="badges"><Status value={session.status} /><span className="badge">{session.type.toUpperCase()}</span>{session.sandbox > 0 && <span className="badge" data-tone="pending">{msg("sandbox")}</span>}</div>
			</div>
			<div className="summary-side">
				<div className="actions">
					<button className="action" type="button" disabled={inspecting} onClick={async () => {
						setInspecting(true);
						try { await onInspect(); } catch { setNotice(msg("couldNotLoadDectaDataPleaseTryAgain")); }
						finally { setInspecting(false); }
					}}><FileJson size={16} />{inspecting ? msg("loading_ba3bbbe1") : msg("dectaData")}</button>
					{session.status === "completed" && session.type !== "refund" && <button className="action" type="button" onClick={onRefund}><RotateCcw size={15} />{msg("refund")}</button>}
				</div>
				<div className="dates"><div><span>{msg("created")}</span><DateTimeTooltip value={session.created_at}>{dateText(session.created_at)}</DateTimeTooltip></div>{session.updated_at && <div><span>{msg("updated")}</span><DateTimeTooltip value={session.updated_at}>{dateText(session.updated_at)}</DateTimeTooltip></div>}</div>
			</div>
		</div>
		{session.fail_reason && <div className="failure"><strong>{msg("failureReason")}</strong>{session.fail_reason}</div>}
		<div className="grid">
			{section(msg("paymentIdentifiers"), <>
				{field(msg("sessionIdSsid"), session.ssid, true)}
				{field(msg("dectaPaymentId"), session.decta_payment_id, true)}
				{field(msg("merchantId_656646fc"), session.merchant_id, true)}
				{field(msg("gateId"), session.gate_id, true)}
				{field(msg("relatedTransactionId"), session.related_transaction_id || null, true, true)}
			</>)}
			{section("Processing & settlement", <>
				{field(msg("status"), session.status, false, false, <Status value={session.status} />)}
				{field(msg("substatus"), session.substatus)}
				{field(msg("settlementStatus"), session.settlement_status, false, false, <Status value={session.settlement_status} />)}
				{field(msg("settlementDate"), session.settlement_date)}
			</>)}
			{section("Customer", <>
				{field(msg("email_969ccbd3"), session.email, true)}
				{field(msg("country"), session.country, false, false, <CountryValue country={session.country} />)}
				{field(msg("ipAddress"), session.client_ip, true, true, session.client_ip ? <div className="value-icon"><Globe2 size={15} color="#667085" aria-hidden="true" /><span>{session.client_ip}</span></div> : undefined)}
			</>)}
			{section("Card", <>
				{field(msg("cardNumber"), session.card_mask, true, true, session.card_mask ? <div className="value-icon"><CreditCard size={16} color="#667085" aria-hidden="true" /><span>{session.card_mask}</span></div> : undefined)}
				{field(msg("network"), session.card_network, false, false, <NetworkValue network={session.card_network} />)}
				{field(msg("bin"), session.card_bin, true)}
			</>)}
		</div>
		<section><h3><ReceiptText size={14} aria-hidden="true" />{msg("fees_6d80486c")}</h3><div className="card"><dl className="fees">
			{field(msg("interchangeFee"), session.interchange_fee || "0")}
			{field(msg("turnoverFee"), session.turnover_fee || "0")}
			{field(msg("schemeFee"), session.scheme_fee || "0")}
		</dl></div></section>
		{antifraud && section("Antifraud", <>
			{field(msg("result"), session.antifraud_status, false, true, <Status value={session.antifraud_status} />)}
			{"success" in antifraud ? <>
				{field(msg("id_3843971d"), antifraud.data.id, true)}
				{field(msg("riskGroup"), antifraud.data.risk_group)}
				{field(msg("riskScore"), antifraud.data.risk_score)}
				{field(msg("tags"), antifraud.data.tags?.join(", "))}
				{field(msg("link"), antifraud.data.link, true, true)}
			</> : <>
				{field(msg("code"), antifraud.code)}
				{field(msg("httpStatus"), antifraud.httpStatus)}
				{field(msg("message_2f77668a"), antifraud.message, false, true)}
			</>}
		</>)}
		{section("Tokens", <>
			{field(msg("payerToken"), session.payer_token, true, true)}
			{field(msg("cardToken"), session.card_token, true, true,
				<span className={session.tokenized === false || session.tokenized === 0 ? "token-not-tokenized" : undefined}>
					{display(session.card_token)}
				</span>)}
		</>)}
		{relatedPayments.length > 0 && <section><h3><ArrowRightLeft size={14} aria-hidden="true" />{msg("relatedPayments")}{relatedPayments.length}</h3><div className="card table-card">
			<table aria-label={msg("relatedPayments_a482d9d2")}><thead><tr><th>{msg("sessionId")}</th><th>{msg("created")}</th><th>{msg("type")}</th><th>{msg("amount")}</th><th>{msg("status")}</th></tr></thead>
				<tbody>{relatedPayments.map((payment, index) => <tr key={`${payment.ssid}-${index}`}>
					<td><button className="payment-link" type="button" onClick={() => onRelated(String(payment.ssid))} aria-label={msg("templateOpenSession_21392008", { v0: payment.ssid })}>{payment.ssid}<ArrowRight size={14} /></button></td>
					<td><DateTimeTooltip value={payment.created_at}>{dateText(payment.created_at)}</DateTimeTooltip></td><td>{payment.type.toUpperCase()}</td>
					<td>{amount(payment.amount, payment.type)} {payment.currency}</td><td><Status value={payment.status} /></td>
				</tr>)}</tbody>
			</table>
		</div></section>}
		{notice && <div className="notice" role="status">{notice}</div>}
	</Layout>;
}
